import React from 'react';
import Icon from '../../../components/AppIcon';
import CollapsiblePanel from './CollapsiblePanel';
import ComponentAnimationPreview from './ComponentAnimationPreview';
import {
  describeComponentAnimationTiming,
  getSectionComponentSequence,
  normalizeSectionAnimation,
  patchSectionAnimation,
} from '../utils/componentAnimation';
import { getComponentDisplayName } from '../utils/defaults';
import ComponentAnimationFields from './ComponentAnimationFields';

function Field({ label, hint, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
      {hint ? <p className="text-[11px] text-muted-foreground/80 leading-snug">{hint}</p> : null}
    </label>
  );
}

function RangeField({ label, value, min, max, step = 1, unit = '', onChange, hint }) {
  return (
    <Field label={`${label}${unit ? ` (${value}${unit})` : ''}`} hint={hint}>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-primary"
      />
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>
          {min}
          {unit}
        </span>
        <span>
          {max}
          {unit}
        </span>
      </div>
    </Field>
  );
}

export default function SectionAnimationFields({ animation, components, onChange }) {
  const config = normalizeSectionAnimation(animation);
  const patch = (next) => onChange(patchSectionAnimation(animation, next));
  const mockSection = { animation: config, components: components || [] };
  const sequence = getSectionComponentSequence(mockSection);

  return (
    <div className="flex flex-col lg:flex-row gap-5 h-full min-h-0">
      <div className="flex-1 min-w-0 space-y-5 overflow-y-auto pr-1 max-h-[min(62vh,560px)]">
        <ComponentAnimationFields
          animation={animation}
          componentType="text"
          mode="section"
          onChange={onChange}
          hidePreview
        />

        {config.enabled ? (
          <>
            <CollapsiblePanel title="Séquence des composants" defaultOpen>
              <div className="space-y-4 pt-1">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-primary"
                    checked={config.staggerEnabled}
                    onChange={(e) => patch({ staggerEnabled: e.target.checked })}
                  />
                  <span>Décalage automatique entre composants synchronisés</span>
                </label>

                {config.staggerEnabled ? (
                  <RangeField
                    label="Intervalle entre composants"
                    value={config.staggerInterval}
                    min={0}
                    max={1500}
                    step={10}
                    unit=" ms"
                    hint="Chaque composant « Avec la section » apparaît après le précédent."
                    onChange={(staggerInterval) => patch({ staggerInterval })}
                  />
                ) : null}

                {sequence.length ? (
                  <div className="rounded-lg border border-border overflow-hidden">
                    <div className="px-3 py-2 bg-muted/30 border-b border-border text-xs font-medium text-muted-foreground">
                      Ordre d’apparition (composants synchronisés)
                    </div>
                    <ul className="divide-y divide-border">
                      {sequence.map((comp, index) => (
                        <li key={comp.id} className="px-3 py-2 flex items-start gap-2 text-xs">
                          <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center">
                            {index + 1}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-foreground truncate">
                              {getComponentDisplayName(comp)}
                            </p>
                            <p className="text-muted-foreground mt-0.5">
                              {describeComponentAnimationTiming(comp, mockSection)}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Ajoutez des composants à la section pour configurer leur séquence.
                  </p>
                )}

                <p className="text-[11px] text-muted-foreground leading-snug">
                  Dans chaque composant → onglet Animation, choisissez « Avec la section » ou « Après un
                  composant » pour contrôler précisément le timing (ex. +10 ms après un texte).
                </p>
              </div>
            </CollapsiblePanel>
          </>
        ) : null}
      </div>

      <div className="lg:w-[300px] shrink-0 lg:sticky lg:top-0 lg:self-start">
        <ComponentAnimationPreview animation={config} componentType="text" tall />
      </div>
    </div>
  );
}
